/**
 * ============================================================
 *  THE LAYOUT CONTRACT
 * ============================================================
 *
 * The shell owns chrome. Pages own content. Neither reaches into
 * the other. Every number the shell needs lives HERE — if a value
 * appears twice in the shell, it belongs in this file instead.
 *
 * ── Scroll ownership ────────────────────────────────────────
 * There is exactly ONE scroll container in the application: the
 * `<main>` element rendered by AppShell, marked `data-scrollable`.
 *
 *   • The page root (`<div>`) is `overflow-hidden` and fixed to the
 *     viewport height — it never scrolls.
 *   • The sidebar has its OWN independent scroll region, because a
 *     62-item nav cannot fit on a laptop. That is the one permitted
 *     exception and it is internal to the sidebar.
 *   • Pages MUST NOT introduce `h-screen`, `overflow-y-auto`, or a
 *     second scroll container. A page that needs its own scroll
 *     region (split-pane queues) opts in via `<AppShell fullHeight>`,
 *     which hands the page a flex column and removes main's padding.
 *
 * ── Spacing ownership ───────────────────────────────────────
 * `<main>` supplies horizontal and vertical page padding, and caps
 * content width. Pages render into that slot and set NO outer
 * margin, padding, or max-width of their own. This is what keeps
 * Home, Patients and Patient Profile optically identical at the
 * page edge without any of them knowing about each other.
 *
 * ── Persistence ─────────────────────────────────────────────
 * The shell must not remount on navigation. Collapse state and open
 * nav groups are persisted to localStorage so they also survive a
 * full reload, not merely a route change.
 * ============================================================
 */

/* ── Dimensions ──────────────────────────────────────────────
   Expressed as CSS custom properties so a single declaration
   drives both the sidebar's own width and the content offset,
   making the two impossible to desynchronise. */
export const SHELL = {
  /** Expanded sidebar rail. */
  sidebarWidth: '17rem', // 272px
  /** Collapsed icon rail — fits a 20px icon plus a 44px touch target. */
  sidebarCollapsedWidth: '4.5rem', // 72px
  /** Off-canvas drawer width on mobile. */
  drawerWidth: '18rem', // 288px
  /** Sticky top bar. */
  headerHeight: '3.5rem', // 56px
  /** Mobile bottom tab bar. */
  bottomNavHeight: '3.75rem', // 60px
  /** Content column cap. Matches DESIGN_SYSTEM.md §1. */
  contentMaxWidth: '90rem', // 1440px
} as const;

/* ── Z-index scale ───────────────────────────────────────────
   Named layers, ordered. Nothing in the shell may use a bare
   `z-*` utility; a magic number here is a stacking bug waiting
   to happen (the old shell had the sidebar and the user dropdown
   both at z-50, so the menu rendered *behind* the rail in RTL).

   Modals/toasts intentionally sit ABOVE everything the shell
   renders — HeroUI portals them to the body. */
export const Z = {
  base: 0,
  bottomNav: 20,
  header: 30,
  drawerScrim: 40,
  drawer: 50,
  /** HeroUI overlays (Popover/Menu/Tooltip/Modal) portal above this. */
  overlay: 60,
} as const;

/* ── Motion ──────────────────────────────────────────────────
   Two durations only: one for colour/opacity swaps, one for
   layout movement. `--ease-out-custom` is defined in theme.css. */
export const MOTION = {
  /** Hover/active colour changes. */
  fast: '120ms',
  /** Width and transform animation (rail collapse, drawer slide). */
  slide: '220ms',
} as const;

/**
 * Inline style object applied to the shell root. Publishing the
 * dimensions as CSS variables lets Tailwind arbitrary values
 * (`w-(--shell-sidebar-w)`) read them, so the numbers above are the
 * only place these sizes are written.
 */
export const shellCssVars = {
  '--shell-sidebar-w': SHELL.sidebarWidth,
  '--shell-sidebar-collapsed-w': SHELL.sidebarCollapsedWidth,
  '--shell-drawer-w': SHELL.drawerWidth,
  '--shell-header-h': SHELL.headerHeight,
  '--shell-bottom-nav-h': SHELL.bottomNavHeight,
  '--shell-content-max-w': SHELL.contentMaxWidth,
  '--shell-motion-fast': MOTION.fast,
  '--shell-motion-slide': MOTION.slide,
} as React.CSSProperties;
